/* eslint-disable @typescript-eslint/no-explicit-any */
import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { isFuture, isPast } from 'date-fns';
import { defineMessages } from 'react-intl';
import type { To } from 'react-router';

import {
  So5FixtureEvent,
  So5LeaderboardSeasonality,
  So5LeaderboardType,
  So5State,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import defaultShield from '@sorare/core/src/assets/club/shield_none.webp';
import {
  ANY_SPORT_PLAY_BOARDS_ARENA_ARENAMODE_FIXTURE_COMPETITION_LINEUPS,
  ANY_SPORT_PLAY_EVENTTYPE_ARENA_FIXTURE_COMPETITION_COMPETITION_TRCKGRP_TRCK,
  ANY_SPORT_PLAY_EVENTTYPE_COMPOSE_LEADERBOARD,
  ANY_SPORT_PLAY_EVENTTYPE_COMPOSE_LEADERBOARD_LINEUP_LINEUP,
  ANY_SPORT_PLAY_EVENTTYPE_COMPOSE_LEADERBOARD_MANAGERTEAM_MANAGERTEAM,
  ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_COMPETITION_COMPETITION_TRCKGRP_TRCK,
} from '@sorare/core/src/constants/__generated__/routes';
import { FRONTEND_ASSET_HOST } from '@sorare/core/src/constants/assets';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { sortBy } from '@sorare/core/src/lib/arrays';
import { isSentInDirectOffer } from '@sorare/core/src/lib/cards';
import { isType, isTypeFn, withFragments } from '@sorare/core/src/lib/gql';
import { generateSportPath } from '@sorare/core/src/lib/routing/generateSportPath';
import { FixtureState } from '@sorare/core/src/lib/so5';
import { localStorage } from '@sorare/core/src/lib/storage/localStorage';

import type {
  getAddTeamPath_so5LeaderboardGroupInterface,
  getBestProjection_playerGameScoreInterface,
  getCompetitionPictureUrl_competition,
  getComposeTeamSuccessRoute_so5Leaderboard,
  getFixtureState_so5Fixture,
  getLeaguesWithContenders_so5LeaderboardGroupInterface,
  getLineupSlotCounts_so5Leaderboard,
  getSo5LeaguesByGroup_so5Fixture,
  getTeamName_team,
  hasGamesStarted_so5Lineup,
  hasLeaderboardGroupExtraSlot_so5LeaderboardGroupInterface,
  hasLeagueTrackExtraSlot_so5LeagueTrack,
  hasSomeCardsForLeaderboard_so5Leaderboard,
  isAppearanceLocked_so5Appearance,
  isCardUsed_anyCard,
  isFixtureClosed_so5Fixture,
  isFixtureLive_so5Fixture,
  isFixtureOpened_so5Fixture,
  isFixtureStarted_so5Fixture,
} from './__generated__/so5.graphql.ts';

export const isFixtureStarted = withFragments(
  (fixture: isFixtureStarted_so5Fixture): boolean =>
    ['started', 'computed', 'closed'].includes(fixture.aasmState),
  {
    so5Fixture: gql`
      fragment isFixtureStarted_so5Fixture on So5Fixture {
        slug
        aasmState
      }
    ` as TypedDocumentNode<isFixtureStarted_so5Fixture>,
  }
);
export const isFixtureOpened = withFragments(
  (fixture?: isFixtureOpened_so5Fixture): boolean =>
    fixture?.aasmState === 'opened',
  {
    so5Fixture: gql`
      fragment isFixtureOpened_so5Fixture on So5Fixture {
        slug
        aasmState
      }
    ` as TypedDocumentNode<isFixtureOpened_so5Fixture>,
  }
);
export const isFixtureLive = withFragments(
  (fixture: Nullable<isFixtureLive_so5Fixture>): boolean =>
    fixture?.aasmState === 'started' &&
    (fixture.live || isFuture(fixture.endDate)),
  {
    so5Fixture: gql`
      fragment isFixtureLive_so5Fixture on So5Fixture {
        slug
        aasmState
        endDate
        live
      }
    ` as TypedDocumentNode<isFixtureLive_so5Fixture>,
  }
);
export const isFixtureClosed = withFragments(
  (fixture: { aasmState: string }): boolean => fixture.aasmState === 'closed',
  {
    so5Fixture: gql`
      fragment isFixtureClosed_so5Fixture on So5Fixture {
        slug
        aasmState
      }
    ` as TypedDocumentNode<isFixtureClosed_so5Fixture>,
  }
);

export const hasGamesStarted = withFragments(
  (so5Lineup: hasGamesStarted_so5Lineup) => {
    return so5Lineup.anyEarliestGame
      ? isPast(so5Lineup.anyEarliestGame.date)
      : true;
  },
  {
    so5Lineup: gql`
      fragment hasGamesStarted_so5Lineup on So5Lineup {
        id
        anyEarliestGame {
          id
          date
        }
      }
    ` as TypedDocumentNode<hasGamesStarted_so5Lineup>,
  }
);

export const getLineupSlotCounts = withFragments(
  (so5Leaderboard: getLineupSlotCounts_so5Leaderboard) => {
    const starters =
      so5Leaderboard.displayedTypedRules.find(isTypeFn('So5AppearancesRule'))
        ?.appearances?.length ?? 0;
    const subs = (so5Leaderboard.rules?.appearances ?? []).filter(
      ({ sub }) => sub
    ).length;
    return { starters, subs };
  },
  {
    so5Leaderboard: gql`
      fragment getLineupSlotCounts_so5Leaderboard on So5Leaderboard {
        id
        slug
        displayedTypedRules {
          id
          ... on So5AppearancesRule {
            id
            appearances {
              id
            }
          }
        }
        rules {
          id
          appearances(includeSubs: true) {
            id
            sub
          }
        }
      }
    ` as TypedDocumentNode<getLineupSlotCounts_so5Leaderboard>,
  }
);

export const getFixtureState = withFragments(
  (so5Fixture: Nullable<getFixtureState_so5Fixture>) => {
    if (!so5Fixture) {
      return undefined;
    }
    if (isFixtureOpened(so5Fixture)) {
      return FixtureState.opened;
    }
    if (isFixtureLive(so5Fixture)) {
      return FixtureState.started;
    }
    if (isFixtureClosed(so5Fixture)) {
      return FixtureState.closed;
    }
    return undefined;
  },
  {
    so5Fixture: gql`
      fragment getFixtureState_so5Fixture on So5Fixture {
        slug
        ...isFixtureOpened_so5Fixture
        ...isFixtureLive_so5Fixture
        ...isFixtureClosed_so5Fixture
      }
      ${isFixtureOpened.fragments.so5Fixture}
      ${isFixtureLive.fragments.so5Fixture}
      ${isFixtureClosed.fragments.so5Fixture}
    ` as TypedDocumentNode<getFixtureState_so5Fixture>,
  }
);

// sorted by display order
export const HANDLED_RULES = [
  'rarityLimits',
  'age',
  'captainRarities',
  'sameActiveClub',
  'seasonBonus',
  'seasonBonusForSpecificTeams',
  'minimumPlayersAverageScore',
  'maximumPlayersAverageScore',
  'scarcity',
  'allowLegend',
  'cardEditionsCount',
  'sumOfAverageScores',
  'averageScores',
  'seasons',
  'leagues',
  'competitions',
  'internationalCompetitions',
  'notDomesticCompetitions',
  'activeClubs',
  'sameNationality',
  'serialNumber',
  'nationalities',
  'notNationalities',
  'atLeastOfCompetitions',
  'atLeastOfClubs',
  'nationalitiesCount',
] as const;

export const seasonalityMessages = defineMessages<So5LeaderboardSeasonality>({
  [So5LeaderboardSeasonality.IN_SEASON]: {
    defaultMessage: 'In season',
  },
  [So5LeaderboardSeasonality.ALL_SEASONS]: {
    defaultMessage: 'Classic Season',
  },
});

export const FIXTURES: Record<string, So5State | undefined> = {
  past: So5State.PAST,
  live: So5State.LIVE,
  liveOrPast: So5State.LIVE_OR_PAST,
  upcoming: So5State.UPCOMING,
  ongoing: So5State.MY_ONGOING,
  onboarding: So5State.ONBOARDING,
  tutorial: So5State.NEXT_WITH_TUTORIAL,
};

export const isClassicOnlyEventTypeSport = (sport: Sport) => {
  return sport === Sport.FOOTBALL || sport === Sport.BASEBALL;
};

// Make sure that old route using /pro dont break queries
export const getValidEventType = (eventType?: string) => {
  const validEventType =
    eventType &&
    Object.values(So5FixtureEvent).includes(
      eventType.toUpperCase() as So5FixtureEvent
    );

  return validEventType
    ? (eventType.toUpperCase() as So5FixtureEvent)
    : So5FixtureEvent.CLASSIC;
};

export const getFixtureQueryVariables = (fixture: string | undefined) => {
  const fixtureType = FIXTURES[fixture!];

  return {
    type: fixtureType,
    slug: fixtureType ? undefined : fixture,
  };
};

export const getComposeTeamRoute = ({
  leaderboardSlug,
  lineupId,
  managerTeamId,
  sport = Sport.FOOTBALL,
  eventType,
  arena,
}: {
  leaderboardSlug: string;
  lineupId?: Nullable<string>;
  managerTeamId?: string;
  sport?: Sport;
  eventType: string;
  arena?: boolean;
}) => {
  let url;

  if (lineupId) {
    url = generateSportPath(
      ANY_SPORT_PLAY_EVENTTYPE_COMPOSE_LEADERBOARD_LINEUP_LINEUP,
      {
        params: {
          leaderboard: leaderboardSlug,
          lineup: idFromObject(lineupId),
          eventType,
        },
        sport,
      }
    );
  } else if (managerTeamId) {
    url = generateSportPath(
      ANY_SPORT_PLAY_EVENTTYPE_COMPOSE_LEADERBOARD_MANAGERTEAM_MANAGERTEAM,
      {
        params: {
          leaderboard: leaderboardSlug,
          managerTeam: idFromObject(managerTeamId),
          eventType,
        },
        sport,
      }
    );
  } else {
    url = generateSportPath(ANY_SPORT_PLAY_EVENTTYPE_COMPOSE_LEADERBOARD, {
      params: { leaderboard: leaderboardSlug, eventType },
      sport,
    });
  }

  return `${url}${arena ? '?arena=true' : ''}`;
};

export const getTeamName = withFragments(
  (team: getTeamName_team | null) => {
    if (!team) {
      return '';
    }
    if (isType(team, 'NationalTeam')) {
      return team.country.name;
    }
    return team.shortName;
  },
  {
    team: gql`
      fragment getTeamName_team on Team {
        ... on Club {
          slug
          shortName
        }
        ... on NationalTeam {
          slug
          country {
            slug
            name
          }
        }
      }
    ` as TypedDocumentNode<getTeamName_team>,
  }
);

export const getCompetitionPictureUrl = withFragments(
  (competition: getCompetitionPictureUrl_competition) => {
    const countryPictureUrl =
      !competition.country || competition.country.slug.includes(' ')
        ? null
        : `${FRONTEND_ASSET_HOST}/flags/${competition.country.slug.toLowerCase()}.svg`;

    const competitionPictureUrl = competition.pictureUrl
      ? competition.pictureUrl
      : countryPictureUrl;

    return competitionPictureUrl || defaultShield;
  },
  {
    competition: gql`
      fragment getCompetitionPictureUrl_competition on Competition {
        slug
        pictureUrl
        country {
          slug
        }
      }
    ` as TypedDocumentNode<getCompetitionPictureUrl_competition>,
  }
);

export const isCardUsed = withFragments(
  ({
    anyCard,
    so5LineupId,
  }: {
    anyCard: isCardUsed_anyCard;
    so5LineupId: string;
  }) => {
    const usedInAnotherLineup =
      isType(anyCard.usedIn, 'UsedInSo5Lineups') &&
      anyCard.usedIn.so5Lineups.some(l => l.id !== so5LineupId);

    return (
      isSentInDirectOffer(anyCard) ||
      usedInAnotherLineup ||
      (anyCard.usedIn && isType(anyCard.usedIn, 'UsedInTasks')) ||
      (anyCard.usedIn && isType(anyCard.usedIn, 'ListedOnMarket'))
    );
  },
  {
    anyCard: gql`
      fragment isCardUsed_anyCard on AnyCardInterface {
        slug
        usedIn(
          so5LeaderboardSlug: $so5LeaderboardSlug
          taskId: $taskId
          gameId: $gameId
        ) {
          slug
          ... on UsedInTasks {
            slug
            tasks {
              id
            }
          }
          ... on UsedInSo5Lineups {
            slug
            so5Lineups {
              id
            }
          }
          ... on ListedOnMarket {
            slug
          }
        }
        ...isSentInDirectOffer_anyCard
      }
      ${isSentInDirectOffer.fragments.anyCard}
    ` as TypedDocumentNode<isCardUsed_anyCard>,
  }
);

export const hasLeagueTrackExtraSlot = withFragments(
  (so5LeagueTrack: hasLeagueTrackExtraSlot_so5LeagueTrack) => {
    const { maxManagerTeamsCount, entrySo5Leaderboard, so5Leaderboards } =
      so5LeagueTrack;

    if (!entrySo5Leaderboard.canCompose.value) {
      return false;
    }

    const managerTeamBased = maxManagerTeamsCount > 0;
    const teamsCap =
      entrySo5Leaderboard.teamsCap !== null
        ? entrySo5Leaderboard.teamsCap
        : Number.POSITIVE_INFINITY;
    const maxCount = managerTeamBased ? maxManagerTeamsCount : teamsCap;

    const contenders = so5Leaderboards.flatMap(
      so5Leaderboard => so5Leaderboard.mySo5LeaderboardContenders
    );

    return maxCount > contenders.length;
  },
  {
    so5LeagueTrack: gql`
      fragment hasLeagueTrackExtraSlot_so5LeagueTrack on So5LeagueTrack {
        slug
        entrySo5Leaderboard {
          slug
          teamsCap
          canCompose {
            value
          }
        }
        so5Leaderboards {
          slug
          mySo5LeaderboardContenders {
            slug
          }
        }
        maxManagerTeamsCount
      }
    ` as TypedDocumentNode<hasLeagueTrackExtraSlot_so5LeagueTrack>,
  }
);

export const hasLeaderboardGroupExtraSlot = withFragments(
  (
    so5LeaderboardGroup: hasLeaderboardGroupExtraSlot_so5LeaderboardGroupInterface
  ) => {
    return so5LeaderboardGroup.so5LeagueTracks.some(so5LeagueTrack =>
      hasLeagueTrackExtraSlot(so5LeagueTrack)
    );
  },
  {
    so5LeaderboardGroupInterface: gql`
      fragment hasLeaderboardGroupExtraSlot_so5LeaderboardGroupInterface on So5LeaderboardGroupInterface {
        slug
        so5LeagueTracks {
          slug
          ...hasLeagueTrackExtraSlot_so5LeagueTrack
        }
      }
      ${hasLeagueTrackExtraSlot.fragments.so5LeagueTrack}
    ` as TypedDocumentNode<hasLeaderboardGroupExtraSlot_so5LeaderboardGroupInterface>,
  }
);

export const getAddTeamPath = withFragments(
  ({
    so5LeaderboardGroupInterface,
    seasonality,
  }: {
    so5LeaderboardGroupInterface?: getAddTeamPath_so5LeaderboardGroupInterface;
    seasonality?: So5LeaderboardSeasonality;
  }) => {
    if (!so5LeaderboardGroupInterface) {
      return null;
    }
    const { so5LeagueTracks, so5Fixture } = so5LeaderboardGroupInterface;

    const composableTracks = so5LeagueTracks.filter(lt => {
      const seasonalityToMatch =
        so5Fixture.sport === Sport.FOOTBALL
          ? lt.entrySo5Leaderboard.seasonality ||
            So5LeaderboardSeasonality.ALL_SEASONS
          : lt.entrySo5Leaderboard.seasonality;

      return (
        lt.entrySo5Leaderboard.canCompose.value &&
        (seasonality === undefined || seasonalityToMatch === seasonality)
      );
    });

    const nextTrackToAdd = composableTracks.find(hasLeagueTrackExtraSlot);

    if (!nextTrackToAdd) {
      return null;
    }

    if (composableTracks.length === 1) {
      return getComposeTeamRoute({
        leaderboardSlug: composableTracks[0].entrySo5Leaderboard.slug,
        lineupId: undefined,
        managerTeamId: undefined,
        sport: so5Fixture.sport,
        eventType: so5Fixture.type.toLowerCase(),
      });
    }

    if (composableTracks.length > 1) {
      return generateSportPath(
        ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_COMPETITION_COMPETITION_TRCKGRP_TRCK,
        {
          sport: so5Fixture.sport,
          params: {
            eventType: so5Fixture.type.toLowerCase(),
            fixture: so5Fixture.slug,
            competition: so5LeaderboardGroupInterface.slug,
            trckGrp: nextTrackToAdd.groupSlug,
            trck: nextTrackToAdd.slug,
          },
        }
      );
    }

    return null;
  },
  {
    so5LeaderboardGroupInterface: gql`
      fragment getAddTeamPath_so5LeaderboardGroupInterface on So5LeaderboardGroupInterface {
        slug
        so5Fixture {
          slug
          type
          sport
        }
        so5LeagueTracks {
          slug
          groupSlug
          entrySo5Leaderboard {
            slug
            seasonality
          }
          ...hasLeagueTrackExtraSlot_so5LeagueTrack
        }
      }
      ${hasLeagueTrackExtraSlot.fragments.so5LeagueTrack}
    ` as TypedDocumentNode<getAddTeamPath_so5LeaderboardGroupInterface>,
  }
);

export const isAppearanceLocked = withFragments(
  (appearance: isAppearanceLocked_so5Appearance) => {
    const lockedAt = appearance?.lockedAt;
    return lockedAt && isPast(lockedAt);
  },
  {
    so5Appearance: gql`
      fragment isAppearanceLocked_so5Appearance on So5AppearanceInterface {
        id
        lockedAt
      }
    ` as TypedDocumentNode<isAppearanceLocked_so5Appearance>,
  }
);

export const getLeaguesWithContenders = withFragments(
  <T extends getLeaguesWithContenders_so5LeaderboardGroupInterface>({
    so5LeaderboardGroups,
    composedOnly,
  }: {
    so5LeaderboardGroups: T[];
    composedOnly?: boolean;
  }): {
    so5LeaderboardGroupsWithContenders: T[];
  } => {
    const so5LeaderboardGroupsWithContenders =
      so5LeaderboardGroups.filter(so5LeaderboardGroup => {
        const composedContenders =
          so5LeaderboardGroup.mySo5LeaderboardContenders.filter(
            ({ so5Lineup }) => so5Lineup
          );
        const contenders = composedOnly
          ? composedContenders
          : so5LeaderboardGroup.mySo5LeaderboardContenders;
        if (contenders.length > 0) {
          return true;
        }

        return false;
      }) || [];

    return {
      so5LeaderboardGroupsWithContenders,
    };
  },
  {
    so5LeadearboardGroupInterface: gql`
      fragment getLeaguesWithContenders_so5LeaderboardGroupInterface on So5LeaderboardGroupInterface {
        slug
        mySo5LeaderboardContenders {
          slug
          so5Lineup {
            id
          }
        }
        ... on So5League {
          slug
          name
        }
      }
    ` as TypedDocumentNode<getLeaguesWithContenders_so5LeaderboardGroupInterface>,
  }
);

export const getComposeTeamSuccessRoute = withFragments(
  (
    so5Leaderboard: getComposeTeamSuccessRoute_so5Leaderboard,
    enableSorare27Nav?: boolean
  ): To => {
    const isProWithArena = localStorage.getItem('arenaSeePro');
    const { so5Fixture, so5LeagueTrack, isArena } = so5Leaderboard;

    if (enableSorare27Nav) {
      return generateSportPath(
        ANY_SPORT_PLAY_BOARDS_ARENA_ARENAMODE_FIXTURE_COMPETITION_LINEUPS,
        {
          sport: so5Fixture.sport,
          params: {
            arenaMode: isArena ? 'essence' : 'cash',
            fixture: so5Fixture.slug,
            competition: so5Leaderboard.slug,
          },
        }
      );
    }

    let so5LeaderboardGroup;

    if (isArena) {
      if (isProWithArena) {
        so5LeaderboardGroup = so5Leaderboard.arenaWithProGroup;
      } else {
        so5LeaderboardGroup = so5Leaderboard.arenaGroup;
      }
    } else {
      so5LeaderboardGroup = so5Leaderboard.competitionGroup;
    }

    return generateSportPath(
      isArena
        ? ANY_SPORT_PLAY_EVENTTYPE_ARENA_FIXTURE_COMPETITION_COMPETITION_TRCKGRP_TRCK
        : ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_COMPETITION_COMPETITION_TRCKGRP_TRCK,
      {
        sport: so5Fixture.sport,
        params: {
          fixture: so5Fixture.slug,
          eventType: so5Fixture.type.toLowerCase(),
          competition: so5LeaderboardGroup.slug,
          trckGrp: so5LeagueTrack.groupSlug,
          trck: so5LeagueTrack.slug,
        },
      }
    );
  },
  {
    so5Leaderboard: gql`
      fragment getComposeTeamSuccessRoute_so5Leaderboard on So5Leaderboard {
        slug
        isArena
        so5Fixture {
          slug
          type
          sport
        }
        arenaGroup: so5LeaderboardGroup(groupType: ARENA) {
          slug
        }
        arenaWithProGroup: so5LeaderboardGroup(
          groupType: COMPETITION_WITH_ARENA
        ) {
          slug
        }
        competitionGroup: so5LeaderboardGroup(groupType: COMPETITION) {
          slug
        }
        so5LeagueTrack {
          slug
          groupSlug
        }
      }
    ` as TypedDocumentNode<getComposeTeamSuccessRoute_so5Leaderboard>,
  }
);

export const hasSomeCardsForLeaderboard = withFragments(
  (so5Leaderboard: hasSomeCardsForLeaderboard_so5Leaderboard) => {
    return so5Leaderboard.eligibleCardsCountByPosition.every(
      ({ usedCardsCount, totalCount }) => totalCount - usedCardsCount === 0
    );
  },
  {
    so5Leaderboard: gql`
      fragment hasSomeCardsForLeaderboard_so5Leaderboard on So5Leaderboard {
        slug
        eligibleCardsCountByPosition {
          slug
          usedCardsCount
          totalCount
        }
      }
    ` as TypedDocumentNode<hasSomeCardsForLeaderboard_so5Leaderboard>,
  }
);

enum GroupKeys {
  SPECIAL_WEEKLY = 'specialWeeklyComposableSo5Leagues',
  MY_COMPOSABLE_SO5_LEAGUES = 'myComposableSo5Leagues',
  ALL_COMPOSABLE_SO5_LEAGUES = 'allComposableSo5Leagues',
}

export const groupTitles = defineMessages({
  [GroupKeys.SPECIAL_WEEKLY]: {
    defaultMessage: 'Special Events',
  },
  [GroupKeys.MY_COMPOSABLE_SO5_LEAGUES]: {
    defaultMessage: 'My Competitions',
  },
  [GroupKeys.ALL_COMPOSABLE_SO5_LEAGUES]: {
    defaultMessage: 'All Competitions',
  },
});

export const getSo5LeaguesByGroup = withFragments(
  <T extends getSo5LeaguesByGroup_so5Fixture>(
    so5Fixture: T,
    composableOnly = true
  ): {
    [key in GroupKeys]: T['so5LeaderboardGroups'];
  } => {
    const { so5LeaderboardGroups } = so5Fixture;
    const composableSo5Leagues = composableOnly
      ? so5LeaderboardGroups.filter(({ canCompose: { value } }) => value)
      : so5LeaderboardGroups;

    return composableSo5Leagues.reduce<{
      [key in GroupKeys]: getSo5LeaguesByGroup_so5Fixture['so5LeaderboardGroups'];
    }>(
      (acc, league) => {
        if (
          league.so5Leaderboards.some(
            so5Leaderboard =>
              so5Leaderboard.so5TournamentType.so5LeaderboardType ===
              So5LeaderboardType.SPECIAL_WEEKLY
          )
        ) {
          acc[GroupKeys.SPECIAL_WEEKLY].push(league);
        } else if (league.mySo5LeaderboardContenders?.length > 0) {
          acc[GroupKeys.MY_COMPOSABLE_SO5_LEAGUES].push(league);
        } else {
          acc[GroupKeys.ALL_COMPOSABLE_SO5_LEAGUES].push(league);
        }
        return acc;
      },
      {
        [GroupKeys.SPECIAL_WEEKLY]: [],
        [GroupKeys.MY_COMPOSABLE_SO5_LEAGUES]: [],
        [GroupKeys.ALL_COMPOSABLE_SO5_LEAGUES]: [],
      }
    );
  },
  {
    so5Fixture: gql`
      fragment getSo5LeaguesByGroup_so5Fixture on So5Fixture {
        slug
        type
        so5LeaderboardGroups(groupType: $groupType) {
          slug
          canCompose {
            value
          }
          so5Leaderboards {
            slug
            so5TournamentType {
              id
              so5LeaderboardType
            }
          }
          mySo5LeaderboardContenders {
            slug
          }
        }
      }
    ` as TypedDocumentNode<getSo5LeaguesByGroup_so5Fixture>,
  }
);

export const getBestEligibleProjection = withFragments(
  <T extends getBestProjection_playerGameScoreInterface>(
    eligiblePlayerGameScores: T[]
  ): T['projection'] | undefined => {
    return (
      sortBy(
        item => item.projection?.grade ?? 'Z',
        [...eligiblePlayerGameScores]
      )[0]?.projection || undefined
    );
  },
  {
    playerGameScoreInterface: gql`
      fragment getBestProjection_playerGameScoreInterface on PlayerGameScoreInterface {
        id
        projection {
          id
          grade
        }
      }
    ` as TypedDocumentNode<getBestProjection_playerGameScoreInterface>,
  }
);
